"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import { FiAlertCircle, FiBarChart2, FiEye, FiRefreshCw, FiSearch, FiSettings, FiTable, FiX } from "react-icons/fi"
import { FaCalendarAlt, FaFileExcel, FaSpinner } from "react-icons/fa"
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    Legend,
    LinearScale,
    Tooltip,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import toast from "react-hot-toast"
import axiosInstance from "../../libs/axios"
import { Pagination as PaginationUtils, PaginationType } from "../../utils/pagination.utils"
import { ExcelDownload, formatExcelDate } from "../../utils/excel.utils"
import { useAuth } from "../../provider/auth.provider"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

/**
 * Leave > Dashboard.
 *
 * One row per employee below the signed-in user in the reporting hierarchy
 * (role level 1 and the HR role see everyone): total, used and balance of each
 * leave type and the leave year. Leave days are read from Attendance (approved
 * Leave Applied days and their leave type), not from the leave applications. "Visualize" swaps the table for charts of the
 * same filtered employees — the figures come from the API's `summary`, so they
 * cover every matching employee, not just the page on screen.
 *
 * Holders of leave.manage_quota also get the Leave Quota panel: the quota of
 * each leave type (global, the same for everyone) and the leave-year start.
 */

interface Person {
    id: number
    name: string
    employee_code?: string | null
}

interface DashboardRow {
    user_id: number
    employee_code?: string | null
    name: string
    reporting_person: Person | null
    reporting_manager: Person | null
    department: { id: number; name: string } | null
    casual_total: number | null
    casual_used: number
    casual_balance: number | null
    medical_used: number
    accidental_used: number
    other_used: number
    total_used: number
    /** Total (quota), used and balance per leave type; null = no fixed quota. */
    leave_types?: Record<string, { total: number | null; used: number; balance: number | null; period: string }>
    leave_year_cycle: string
}

interface LeaveYear {
    year: number
    start_date: string
    end_date: string
    label: string
}

interface LeavePolicy {
    leave_type: string
    quota: number | null
    period: "monthly" | "yearly"
    block_over_quota: boolean
}

interface DashboardSummary {
    employees: number
    employees_with_leave: number
    total_used: number
    by_type: Record<string, number>
    by_department: Array<Record<string, any>>
    top_users: Array<{ user_id: number; name: string; employee_code?: string | null; total_used: number }>
}

const LEAVE_TYPES: Array<{ key: string; label: string; color: string }> = [
    { key: "casual", label: "Casual", color: "#2a78d6" },
    { key: "medical", label: "Medical", color: "#eb6834" },
    { key: "accidental", label: "Accidental", color: "#1baf7a" },
    { key: "other", label: "Special Approval", color: "#e87ba4" },
]

// Identity columns, three per leave type, Total Used, the leave year and Actions.
const TABLE_COLUMN_COUNT = 5 + LEAVE_TYPES.length * 3 + 3

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
]

const EMPTY_SUMMARY: DashboardSummary = {
    employees: 0,
    employees_with_leave: 0,
    total_used: 0,
    by_type: {},
    by_department: [],
    top_users: [],
}

const personText = (person: Person | null) => person?.name || "—"
const balanceText = (value: number | null) => (value === null || value === undefined ? "No quota" : String(value))

/** Total / used / balance of one leave type for a row. */
const leaveTypeFigures = (row: DashboardRow, key: string) =>
    row.leave_types?.[key] ?? { total: null, used: 0, balance: null, period: "yearly" }

const toExportRow = (row: DashboardRow) => ({
    "E-Code": row.employee_code || "",
    "User Name": row.name,
    "Reporting Person": row.reporting_person?.name || "",
    "Reporting Manager": row.reporting_manager?.name || "",
    "Department": row.department?.name || "",
    ...Object.fromEntries(
        LEAVE_TYPES.flatMap(({ key, label }) => {
            const figures = leaveTypeFigures(row, key)
            return [
                [`${label} Total`, figures.total === null ? "No quota" : figures.total],
                [`${label} Used`, figures.used],
                [`${label} Balance`, figures.balance === null ? "No quota" : figures.balance],
            ]
        })
    ),
    "Total Used": row.total_used,
    "Leave Yearly Period Cycle": row.leave_year_cycle,
})

// ─── View Leaves modal ───────────────────────────────────────────────────────

interface EmployeeLeaveRow {
    attendance_id: number
    employee_code: string | null
    name: string
    leave_year_cycle: string
    leave_date: string
    leave_type: string
    leave_type_label: string
    marked_by: Person | null
    department: { id: number; name: string } | null
    reporting_person: Person | null
    approved_by: Person | null
}

/** dd-mm-yyyy of a YYYY-MM-DD day, read as written (no time zone shift). */
const formatLeaveDate = (value: string) => formatExcelDate(value, value || "—")

const toLeaveExportRow = (row: EmployeeLeaveRow) => ({
    "Employee Code": row.employee_code || "",
    "Name": row.name,
    "Year Cycle": row.leave_year_cycle,
    "Leave Date": formatExcelDate(row.leave_date),
    "Marked By": row.marked_by?.name || "",
    "Department": row.department?.name || "",
    "Reporting Person": row.reporting_person?.name || "",
    "Leave Type": row.leave_type_label,
    "Approved By": row.approved_by?.name || "",
})

/**
 * Every leave day of one employee in a leave year — the days their dashboard
 * row counts — with who marked and who approved each, and an Excel export.
 */
const EmployeeLeavesModal: React.FC<{
    employee: DashboardRow | null
    initialYear: string
    leaveYears: LeaveYear[]
    onClose: () => void
}> = ({ employee, initialYear, leaveYears, onClose }) => {
    const [year, setYear] = useState(initialYear)
    const [leaves, setLeaves] = useState<EmployeeLeaveRow[]>([])
    const [leaveYear, setLeaveYear] = useState<LeaveYear | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [showExport, setShowExport] = useState(false)

    useEffect(() => {
        if (employee) setYear(initialYear)
    }, [employee, initialYear])

    useEffect(() => {
        if (!employee) return
        let cancelled = false
        setLoading(true)
        setError("")
        axiosInstance
            .get(`/leave/dashboard/${employee.user_id}/leaves`, { params: year ? { year } : {} })
            .then((response) => {
                if (cancelled) return
                const data = response.data?.data || {}
                setLeaves(data.leaves || [])
                setLeaveYear(data.leave_year || null)
            })
            .catch((err: any) => {
                if (cancelled) return
                setLeaves([])
                setError(err.response?.data?.message || "Failed to load leaves")
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [employee, year])

    if (!employee) return null

    const headCell = "px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase whitespace-nowrap"
    const cell = "px-3 py-2 text-sm text-gray-700 whitespace-nowrap"
    const columnCount = 9

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/40" onClick={onClose} />
                <div className="relative bg-white rounded shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                                Leaves — {employee.name}
                                {employee.employee_code ? ` (${employee.employee_code})` : ""}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {loading
                                    ? "Loading..."
                                    : `${leaves.length} leave day${leaves.length === 1 ? "" : "s"}${leaveYear ? ` in ${leaveYear.label}` : ""}, as recorded in Attendance.`}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <select
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                aria-label="Leave year"
                                className="px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Current leave year</option>
                                {leaveYears.map((item) => (
                                    <option key={item.year} value={item.year}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => setShowExport(true)}
                                disabled={loading || leaves.length === 0}
                                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                            >
                                <FaFileExcel className="w-4 h-4" />
                                Export
                            </button>
                            <button onClick={onClose} aria-label="Close" className="p-2 text-gray-500 hover:text-gray-800">
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="mx-4 sm:mx-6 mt-4 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div className="overflow-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className={headCell}>Employee Code</th>
                                    <th className={headCell}>Name</th>
                                    <th className={headCell}>Year Cycle</th>
                                    <th className={headCell}>Leave Date</th>
                                    <th className={headCell}>Marked By</th>
                                    <th className={headCell}>Department</th>
                                    <th className={headCell}>Reporting Person</th>
                                    <th className={headCell}>Leave Type</th>
                                    <th className={headCell}>Approved By</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={columnCount} className="px-4 py-10 text-center text-sm text-gray-600">
                                            <FaSpinner className="inline w-4 h-4 animate-spin mr-2" />
                                            Loading...
                                        </td>
                                    </tr>
                                ) : leaves.length === 0 ? (
                                    <tr>
                                        <td colSpan={columnCount} className="px-4 py-10 text-center text-sm text-gray-500">
                                            No leave recorded in this leave year.
                                        </td>
                                    </tr>
                                ) : (
                                    leaves.map((row) => (
                                        <tr key={row.attendance_id} className="hover:bg-gray-50">
                                            <td className={cell}>{row.employee_code || "—"}</td>
                                            <td className={`${cell} font-medium text-gray-900`}>{row.name}</td>
                                            <td className={cell}>{row.leave_year_cycle}</td>
                                            <td className={cell}>{formatLeaveDate(row.leave_date)}</td>
                                            <td className={cell}>{personText(row.marked_by)}</td>
                                            <td className={cell}>{row.department?.name || "—"}</td>
                                            <td className={cell}>{personText(row.reporting_person)}</td>
                                            <td className={cell}>{row.leave_type_label}</td>
                                            <td className={cell}>{personText(row.approved_by)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <ExcelDownload
                isOpen={showExport}
                onClose={() => setShowExport(false)}
                data={leaves.map(toLeaveExportRow)}
                filename={`leaves_${employee.employee_code || employee.user_id}`}
                title="Employee Leaves Export"
                description={`Leaves of ${employee.name}${leaveYear ? ` — ${leaveYear.label}` : ""}`}
            />
        </>
    )
}

// ─── Leave Quota panel ───────────────────────────────────────────────────────

const LeaveQuotaOffcanvas: React.FC<{
    isOpen: boolean
    onClose: () => void
    onSaved: () => void
}> = ({ isOpen, onClose, onSaved }) => {
    const [startMonth, setStartMonth] = useState(1)
    const [policies, setPolicies] = useState<LeavePolicy[]>([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")
    const [updatedInfo, setUpdatedInfo] = useState("")

    useEffect(() => {
        if (!isOpen) return
        setLoading(true)
        setError("")
        axiosInstance
            .get("/leave/policies")
            .then((response) => {
                const data = response.data?.data || {}
                setStartMonth(Number(data.year_start_month) || 1)
                setPolicies(data.policies || [])
                const by = data.updated_by ? `${data.updated_by.first_name} ${data.updated_by.last_name}`.trim() : ""
                setUpdatedInfo(
                    data.updated_at
                        ? `Last changed ${new Date(data.updated_at).toLocaleString("en-IN")}${by ? ` by ${by}` : ""}`
                        : "Using the default quotas — nothing has been changed yet."
                )
            })
            .catch((err) => setError(err.response?.data?.message || "Failed to load leave quotas"))
            .finally(() => setLoading(false))
    }, [isOpen])

    if (!isOpen) return null

    const setPolicy = (leaveType: string, changes: Partial<LeavePolicy>) =>
        setPolicies((prev) => prev.map((policy) => (policy.leave_type === leaveType ? { ...policy, ...changes } : policy)))

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault()
        setSaving(true)
        setError("")
        try {
            await axiosInstance.put("/leave/policies", { year_start_month: startMonth, policies })
            toast.success("Leave quotas updated")
            onSaved()
            onClose()
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to save leave quotas")
        } finally {
            setSaving(false)
        }
    }

    const endMonth = MONTHS[(startMonth + 10) % 12]

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
            <div className="absolute right-0 top-0 h-full w-full sm:max-w-xl bg-white shadow-xl overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Leave Quota</h2>
                        <p className="text-xs text-gray-500">Applies to every employee.</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <FiX className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {loading ? (
                    <div className="p-8 text-center text-sm text-gray-600">Loading...</div>
                ) : (
                    <form onSubmit={handleSave} className="p-4 space-y-5">
                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
                                <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-red-700">{error}</span>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Leave year starts in</label>
                            <select
                                value={startMonth}
                                onChange={(e) => setStartMonth(Number(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                                {MONTHS.map((month, index) => (
                                    <option key={month} value={index + 1}>
                                        {month}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                                The leave year runs {MONTHS[startMonth - 1]} to {endMonth}. Yearly quotas reset when it starts.
                            </p>
                        </div>

                        <div className="space-y-3">
                            {policies.map((policy) => {
                                const label =
                                    LEAVE_TYPES.find((type) => type.key === policy.leave_type)?.label || policy.leave_type
                                const hasQuota = policy.quota !== null
                                return (
                                    <div key={policy.leave_type} className="border border-gray-200 rounded p-3">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-semibold text-gray-900">{label} Leave</span>
                                            <label className="flex items-center gap-2 text-xs text-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={hasQuota}
                                                    onChange={(e) =>
                                                        setPolicy(policy.leave_type, { quota: e.target.checked ? 0 : null })
                                                    }
                                                    className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                Fixed quota
                                            </label>
                                        </div>
                                        {hasQuota ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Days</label>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={366}
                                                        step={1}
                                                        value={policy.quota ?? 0}
                                                        onChange={(e) =>
                                                            setPolicy(policy.leave_type, {
                                                                quota: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                                                            })
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Per</label>
                                                    <select
                                                        value={policy.period}
                                                        onChange={(e) =>
                                                            setPolicy(policy.leave_type, {
                                                                period: e.target.value as LeavePolicy["period"],
                                                            })
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                                    >
                                                        <option value="yearly">Leave year</option>
                                                        <option value="monthly">Month (unused days lapse)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Beyond quota</label>
                                                    <select
                                                        value={policy.block_over_quota ? "block" : "allow"}
                                                        onChange={(e) =>
                                                            setPolicy(policy.leave_type, {
                                                                block_over_quota: e.target.value === "block",
                                                            })
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                                    >
                                                        <option value="block">Block the application</option>
                                                        <option value="allow">Allow, flag for approver</option>
                                                    </select>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-500">
                                                No fixed quota — employees can apply for any number of days and the approver decides.
                                            </p>
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        <p className="text-xs text-gray-500">{updatedInfo}</p>

                        <div className="flex gap-3 pt-2">
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex-1 bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 transition-colors text-sm font-medium disabled:opacity-50"
                            >
                                {saving ? "Saving..." : "Save Quotas"}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

// ─── Charts ──────────────────────────────────────────────────────────────────

const CHART_TEXT = "#374151"
const CHART_GRID = "#eef0f3"

const baseBarOptions = (horizontal: boolean, stacked: boolean, showLegend: boolean): any => ({
    indexAxis: horizontal ? "y" : "x",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: showLegend,
            position: "top",
            align: "start",
            labels: { color: CHART_TEXT, boxWidth: 12, boxHeight: 12, font: { size: 12 } },
        },
        tooltip: {
            callbacks: {
                label: (context: any) =>
                    `${context.dataset.label}: ${context.parsed[horizontal ? "x" : "y"]} day(s)`,
            },
        },
    },
    scales: {
        x: {
            stacked,
            beginAtZero: true,
            grid: { display: horizontal, color: CHART_GRID },
            ticks: { color: CHART_TEXT, precision: 0 },
        },
        y: {
            stacked,
            beginAtZero: true,
            grid: { display: !horizontal, color: CHART_GRID },
            ticks: { color: CHART_TEXT, precision: 0 },
        },
    },
})

const ChartCard: React.FC<{ title: string; subtitle: string; height: number; empty: boolean; children: React.ReactNode }> = ({
    title,
    subtitle,
    height,
    empty,
    children,
}) => (
    <div className="border border-gray-200 rounded p-4">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500 mb-3">{subtitle}</p>
        {empty ? (
            <div className="flex items-center justify-center text-sm text-gray-500" style={{ height }}>
                No leave taken in this leave year.
            </div>
        ) : (
            <div style={{ height }}>{children}</div>
        )}
    </div>
)

const DashboardCharts: React.FC<{ summary: DashboardSummary }> = ({ summary }) => {
    const empty = summary.total_used === 0
    const departments = summary.by_department.filter((item) => item.total > 0).slice(0, 12)

    const byType = {
        labels: LEAVE_TYPES.map((type) => type.label),
        datasets: [
            {
                label: "Days used",
                data: LEAVE_TYPES.map((type) => summary.by_type[type.key] || 0),
                backgroundColor: LEAVE_TYPES.map((type) => type.color),
                borderRadius: 4,
                maxBarThickness: 48,
            },
        ],
    }

    // Colour follows the leave type everywhere on the page.
    const byDepartment = {
        labels: departments.map((item) => item.department),
        datasets: LEAVE_TYPES.map((type) => ({
            label: type.label,
            data: departments.map((item) => item[type.key] || 0),
            backgroundColor: type.color,
            borderColor: "#ffffff",
            borderWidth: 1,
            maxBarThickness: 28,
        })),
    }

    const topUsers = {
        labels: summary.top_users.map((user) =>
            user.employee_code ? `${user.name} (${user.employee_code})` : user.name
        ),
        datasets: [
            {
                label: "Total days used",
                data: summary.top_users.map((user) => user.total_used),
                backgroundColor: "#2a78d6",
                borderRadius: 4,
                maxBarThickness: 22,
            },
        ],
    }

    return (
        <div className="p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
            <ChartCard
                title="Leave days by type"
                subtitle="All employees matching the filters, whole leave year."
                height={280}
                empty={empty}
            >
                <Bar data={byType} options={baseBarOptions(false, false, false)} />
            </ChartCard>
            <ChartCard
                title="Top 10 employees by leave taken"
                subtitle="Total days used in the leave year."
                height={280}
                empty={empty}
            >
                <Bar data={topUsers} options={baseBarOptions(true, false, false)} />
            </ChartCard>
            <div className="xl:col-span-2">
                <ChartCard
                    title="Leave days by department"
                    subtitle={
                        summary.by_department.length > departments.length
                            ? `The ${departments.length} departments with the most leave, split by leave type.`
                            : "Split by leave type."
                    }
                    height={Math.max(220, departments.length * 38 + 80)}
                    empty={empty}
                >
                    <Bar data={byDepartment} options={baseBarOptions(true, true, true)} />
                </ChartCard>
            </div>
        </div>
    )
}

// ─── Page ────────────────────────────────────────────────────────────────────

const LeaveDashboard: React.FC = () => {
    const { user, hasPermission } = useAuth()
    const canManageQuota = hasPermission("leave", "manage_quota")
    const isUnrestricted = user?.role?.level === 1 || user?.role?.name?.toLowerCase() === "hr"

    const [rows, setRows] = useState<DashboardRow[]>([])
    const [summary, setSummary] = useState<DashboardSummary>(EMPTY_SUMMARY)
    const [leaveYear, setLeaveYear] = useState<LeaveYear | null>(null)
    const [policies, setPolicies] = useState<LeavePolicy[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [view, setView] = useState<"table" | "charts">("table")
    const [searchInput, setSearchInput] = useState("")
    const [search, setSearch] = useState("")
    const [departmentId, setDepartmentId] = useState("")
    const [year, setYear] = useState("")
    const [departments, setDepartments] = useState<Array<{ id: number; name: string }>>([])
    const [leaveYears, setLeaveYears] = useState<LeaveYear[]>([])
    const [pagination, setPagination] = useState<PaginationType>({
        page: 1,
        limit: 25,
        total_count: 0,
        total_pages: 0,
        offset: 0,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    })
    const [quotaOpen, setQuotaOpen] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [exportData, setExportData] = useState<any[]>([])
    const [showExport, setShowExport] = useState(false)
    const [viewingEmployee, setViewingEmployee] = useState<DashboardRow | null>(null)

    useEffect(() => {
        const handle = setTimeout(() => setSearch(searchInput.trim()), 350)
        return () => clearTimeout(handle)
    }, [searchInput])

    const loadFilterOptions = useCallback(() => {
        axiosInstance
            .get("/leave/dashboard/filter-options")
            .then((response) => {
                const data = response.data?.data || {}
                setDepartments(data.departments || [])
                setLeaveYears(data.leave_years || [])
            })
            .catch(() => {
                // The table still works without dropdown options.
            })
    }, [])

    useEffect(() => {
        loadFilterOptions()
    }, [loadFilterOptions])

    const buildParams = useCallback(
        (page: number, limit: number) => {
            const params: Record<string, string | number> = { page, limit }
            if (search) params.search = search
            if (departmentId) params.department_id = departmentId
            if (year) params.year = year
            return params
        },
        [search, departmentId, year]
    )

    const fetchRows = useCallback(
        async (page = 1, limit = pagination.limit) => {
            setLoading(true)
            setError("")
            try {
                const response = await axiosInstance.get("/leave/dashboard", { params: buildParams(page, limit) })
                const data = response.data?.data || {}
                setRows(data.employees || [])
                setSummary(data.summary || EMPTY_SUMMARY)
                setLeaveYear(data.leave_year || null)
                setPolicies(data.policies || [])
                if (data.pagination) setPagination(data.pagination)
            } catch (err: any) {
                setError(err.response?.data?.message || "Failed to load the leave dashboard")
                setRows([])
                setSummary(EMPTY_SUMMARY)
            } finally {
                setLoading(false)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [buildParams]
    )

    useEffect(() => {
        fetchRows(1, pagination.limit)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, departmentId, year])

    const casualPolicy = useMemo(() => policies.find((policy) => policy.leave_type === "casual"), [policies])
    const activeFilterCount = (search ? 1 : 0) + (departmentId ? 1 : 0) + (year ? 1 : 0)

    const clearFilters = () => {
        setSearchInput("")
        setDepartmentId("")
        setYear("")
    }

    const handleExport = async () => {
        setExporting(true)
        try {
            const all: DashboardRow[] = []
            let page = 1
            let totalPages = 1
            do {
                const response = await axiosInstance.get("/leave/dashboard", { params: buildParams(page, 500) })
                const data = response.data?.data || {}
                all.push(...(data.employees || []))
                totalPages = Math.max(1, Number(data.pagination?.total_pages) || 1)
                page += 1
            } while (page <= totalPages)
            if (all.length === 0) {
                toast.error("No employees found for the current filters")
                return
            }
            setExportData(all.map(toExportRow))
            setShowExport(true)
        } catch (err: any) {
            toast.error(err.response?.data?.message || "Failed to prepare export")
        } finally {
            setExporting(false)
        }
    }

    const tiles = [
        { label: "Employees", value: summary.employees },
        { label: "Took leave", value: summary.employees_with_leave },
        { label: "Total days used", value: summary.total_used },
        ...LEAVE_TYPES.map((type) => ({ label: `${type.label} days`, value: summary.by_type[type.key] || 0 })),
    ]

    const headCell = "px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase whitespace-nowrap"
    const cell = "px-4 py-4 text-sm text-gray-700 whitespace-nowrap"
    const numberCell = `${cell} text-right tabular-nums`

    return (
        <>
            <div className="p-4 space-y-4">
                <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
                    {/* Header */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded bg-blue-50 text-blue-800 flex items-center justify-center">
                                    <FaCalendarAlt className="w-5 h-5" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-semibold text-gray-900">Leave Dashboard</h1>
                                    <p className="text-sm text-gray-500">
                                        {isUnrestricted
                                            ? "Leave total, used and balance of every employee, as recorded in Attendance."
                                            : "Leave total, used and balance of everyone who reports to you, directly or through your team, as recorded in Attendance."}
                                        {leaveYear ? ` Leave year ${leaveYear.label}.` : ""}
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={() => setView(view === "table" ? "charts" : "table")}
                                    className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 bg-white"
                                >
                                    {view === "table" ? <FiBarChart2 className="w-4 h-4" /> : <FiTable className="w-4 h-4" />}
                                    {view === "table" ? "Visualize" : "Show Table"}
                                </button>
                                <button
                                    onClick={() => fetchRows(pagination.page, pagination.limit)}
                                    className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 bg-white"
                                >
                                    <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                    Refresh
                                </button>
                                {canManageQuota && (
                                    <button
                                        onClick={() => setQuotaOpen(true)}
                                        className="px-3 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 text-sm font-medium flex items-center gap-2"
                                    >
                                        <FiSettings className="w-4 h-4" />
                                        Leave Quota
                                    </button>
                                )}
                                <button
                                    onClick={handleExport}
                                    disabled={exporting}
                                    className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                                >
                                    {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
                                    {exporting ? "Preparing..." : "Export"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                            <div className="lg:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Employee</label>
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        placeholder="Name, E-Code or email"
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
                                <select
                                    value={departmentId}
                                    onChange={(e) => setDepartmentId(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All departments</option>
                                    {departments.map((department) => (
                                        <option key={department.id} value={department.id}>
                                            {department.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Leave Year</label>
                                <select
                                    value={year}
                                    onChange={(e) => setYear(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Current leave year</option>
                                    {leaveYears.map((item) => (
                                        <option key={item.year} value={item.year}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {activeFilterCount > 0 && (
                            <div className="mt-3 flex items-center gap-2 text-sm">
                                <span className="text-gray-600">
                                    {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied
                                </span>
                                <button onClick={clearFilters} className="text-blue-700 hover:text-blue-900 underline">
                                    Clear all
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Figures */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
                        {tiles.map((tile) => (
                            <div key={tile.label} className="border border-gray-200 rounded px-3 py-2">
                                <div className="text-xs text-gray-500">{tile.label}</div>
                                <div className="text-xl font-semibold text-gray-900 tabular-nums">
                                    {Number(tile.value).toLocaleString("en-IN")}
                                </div>
                            </div>
                        ))}
                    </div>

                    {error && (
                        <div className="mx-4 sm:mx-6 mt-4 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {view === "charts" ? (
                        <DashboardCharts summary={summary} />
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th rowSpan={2} className={headCell}>E-Code</th>
                                            <th rowSpan={2} className={headCell}>User Name</th>
                                            <th rowSpan={2} className={headCell}>Reporting Person</th>
                                            <th rowSpan={2} className={headCell}>Reporting Manager</th>
                                            <th rowSpan={2} className={headCell}>Department</th>
                                            {LEAVE_TYPES.map((type) => (
                                                <th key={type.key} colSpan={3} className={`${headCell} text-center border-l border-gray-200`}>
                                                    {type.label}
                                                </th>
                                            ))}
                                            <th rowSpan={2} className={`${headCell} text-right border-l border-gray-200`}>Total Used</th>
                                            <th rowSpan={2} className={headCell}>Leave Yearly Period Cycle</th>
                                            <th rowSpan={2} className={headCell}>Actions</th>
                                        </tr>
                                        <tr>
                                            {LEAVE_TYPES.map((type) => (
                                                <React.Fragment key={type.key}>
                                                    <th className={`${headCell} text-right border-l border-gray-200`}>Total</th>
                                                    <th className={`${headCell} text-right`}>Used</th>
                                                    <th className={`${headCell} text-right`}>Balance</th>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {loading ? (
                                            <tr>
                                                <td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-12 text-center text-sm text-gray-600">
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : rows.length === 0 ? (
                                            <tr>
                                                <td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-12 text-center text-sm text-gray-500">
                                                    {activeFilterCount > 0
                                                        ? "No employees match these filters."
                                                        : "Nobody reports to you, so there is nothing to show here."}
                                                </td>
                                            </tr>
                                        ) : (
                                            rows.map((row) => (
                                                <tr key={row.user_id} className="hover:bg-gray-50">
                                                    <td className={cell}>{row.employee_code || "—"}</td>
                                                    <td className={`${cell} font-medium text-gray-900`}>{row.name}</td>
                                                    <td className={cell}>{personText(row.reporting_person)}</td>
                                                    <td className={cell}>{personText(row.reporting_manager)}</td>
                                                    <td className={cell}>{row.department?.name || "—"}</td>
                                                    {LEAVE_TYPES.map((type) => {
                                                        const figures = leaveTypeFigures(row, type.key)
                                                        return (
                                                            <React.Fragment key={type.key}>
                                                                <td className={`${numberCell} border-l border-gray-100`}>{balanceText(figures.total)}</td>
                                                                <td className={numberCell}>{figures.used}</td>
                                                                <td
                                                                    className={`${numberCell} ${figures.balance !== null && figures.balance < 0 ? "text-red-700 font-medium" : ""}`}
                                                                >
                                                                    {balanceText(figures.balance)}
                                                                </td>
                                                            </React.Fragment>
                                                        )
                                                    })}
                                                    <td className={`${numberCell} font-semibold text-gray-900 border-l border-gray-100`}>{row.total_used}</td>
                                                    <td className={cell}>{row.leave_year_cycle}</td>
                                                    <td className={cell}>
                                                        <button
                                                            onClick={() => setViewingEmployee(row)}
                                                            className="px-2.5 py-1.5 border border-blue-200 text-blue-800 bg-blue-50 rounded text-xs font-medium hover:bg-blue-100 flex items-center gap-1.5"
                                                        >
                                                            <FiEye className="w-3.5 h-3.5" />
                                                            View Leaves
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {casualPolicy && casualPolicy.quota !== null && (
                                <p className="px-4 sm:px-6 py-2 text-xs text-gray-500 border-t border-gray-100">
                                    Casual quota: {casualPolicy.quota} day(s) per{" "}
                                    {casualPolicy.period === "monthly"
                                        ? "month — Casual Used and Balance are for the current month"
                                        : "leave year"}
                                    .
                                </p>
                            )}
                            {pagination.total_count > 0 && (
                                <div className="border-t border-gray-200">
                                    <PaginationUtils
                                        pagination={pagination}
                                        onPageChange={(page) => fetchRows(page, pagination.limit)}
                                        onLimitChange={(limit) => fetchRows(1, limit)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <LeaveQuotaOffcanvas
                isOpen={quotaOpen}
                onClose={() => setQuotaOpen(false)}
                onSaved={() => {
                    loadFilterOptions()
                    fetchRows(1, pagination.limit)
                }}
            />

            <EmployeeLeavesModal
                employee={viewingEmployee}
                initialYear={year}
                leaveYears={leaveYears}
                onClose={() => setViewingEmployee(null)}
            />

            <ExcelDownload
                isOpen={showExport}
                onClose={() => setShowExport(false)}
                data={exportData}
                filename="leave_dashboard"
                title="Leave Dashboard Export"
                description={`Leave used and balance per employee${leaveYear ? ` — ${leaveYear.label}` : ""}`}
            />
        </>
    )
}

export default LeaveDashboard
